import {
  MapPin,
  Plus,
  Pencil,
  Trash2,
  Home,
  Building2,
  MapPinned,
  X,
  Save,
  Star,
  AlertCircle,
  Phone,
  UserRound,
  Navigation,
  CheckCircle2,
} from "lucide-react";

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import "../../styles/Addresses.css";
import ProfilePageLayout from "../../components/profile/ProfilePageLayout";
import { useAuth } from "../../context/AuthContext";


// =========================================================
// API
// =========================================================

const API_BASE_URL =
  import.meta.env.VITE_API_URL ||
  (
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1"
      ? "http://localhost:8000/api"
      : "https://api.laesquinadelparques.com/api"
  );

const ADDRESSES_URL = `${API_BASE_URL}/addresses/`;


// =========================================================
// EMPTY FORM
// =========================================================

const EMPTY_FORM = {
  title: "",
  name: "",
  address: "",
  neighborhood: "",
  city: "",
  department: "",
  country: "Colombia",
  phone: "",
  reference: "",
  type: "home",
  principal: false,
  latitude: "",
  longitude: "",
  formatted_address: "",
  google_place_id: "",
};


// =========================================================
// API ERROR
// =========================================================

async function getApiError(response) {
  try {
    const data = await response.json();

    if (data?.detail) {
      return data.detail;
    }

    if (data?.message) {
      return data.message;
    }

    if (data?.error) {
      return data.error;
    }

    if (data?.errors) {
      const errors = data.errors;

      if (typeof errors === "string") {
        return errors;
      }

      if (
        typeof errors === "object" &&
        errors !== null
      ) {
        return Object.entries(errors)
          .map(([field, messages]) => {
            const text = Array.isArray(messages)
              ? messages.join(", ")
              : String(messages);

            return `${field}: ${text}`;
          })
          .join(" | ");
      }
    }

    if (
      typeof data === "object" &&
      data !== null
    ) {
      const entries = Object.entries(data);

      if (entries.length > 0) {
        return entries
          .map(([field, messages]) => {
            const text = Array.isArray(messages)
              ? messages.join(", ")
              : String(messages);

            return `${field}: ${text}`;
          })
          .join(" | ");
      }
    }

    return "Ocurrió un error inesperado.";
  } catch {
    return `Error ${response.status}`;
  }
}


// =========================================================
// API REQUEST
// =========================================================

async function apiRequest(
  url,
  options = {},
  accessToken = null,
) {
  const headers = {
    Accept: "application/json",
    ...options.headers,
  };

  if (
    options.body &&
    !(options.body instanceof FormData)
  ) {
    headers["Content-Type"] = "application/json";
  }

  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  const response = await fetch(
    url,
    {
      ...options,
      headers,
    },
  );

  if (!response.ok) {
    const message = await getApiError(response);

    throw new Error(message);
  }

  if (response.status === 204) {
    return null;
  }

  const contentType =
    response.headers.get("content-type");

  if (
    !contentType ||
    !contentType.includes("application/json")
  ) {
    return null;
  }

  return response.json();
}


// =========================================================
// TYPE LABEL
// =========================================================

function getTypeLabel(type) {
  switch (type) {
    case "home":
      return "Casa";

    case "work":
      return "Trabajo";

    case "other":
      return "Otra";

    default:
      return "Dirección";
  }
}


// =========================================================
// ADDRESS ICON
// =========================================================

function getAddressIcon(type) {
  switch (type) {
    case "home":
      return Home;

    case "work":
      return Building2;

    default:
      return MapPinned;
  }
}


// =========================================================
// NORMALIZE ADDRESS
// =========================================================

function normalizeAddress(address) {
  if (!address) {
    return null;
  }

  return {
    id: address.id,

    title:
      address.title ||
      "",

    name:
      address.name ||
      "",

    address:
      address.address ||
      "",

    neighborhood:
      address.neighborhood ||
      "",

    city:
      address.city ||
      "",

    department:
      address.department ||
      "",

    country:
      address.country ||
      "Colombia",

    phone:
      address.phone ||
      "",

    reference:
      address.reference ||
      "",

    type:
      address.type ||
      "home",

    typeLabel:
      address.type_label ||
      getTypeLabel(address.type),

    principal:
      Boolean(address.principal),

    latitude:
      address.latitude ??
      "",

    longitude:
      address.longitude ??
      "",

    formatted_address:
      address.formatted_address ||
      "",

    google_place_id:
      address.google_place_id ||
      "",

    created_at:
      address.created_at ||
      null,

    updated_at:
      address.updated_at ||
      null,
  };
}


// =========================================================
// EXTRACT ADDRESS LIST
// =========================================================

function extractAddressList(response) {
  if (Array.isArray(response)) {
    return response;
  }

  if (Array.isArray(response?.results)) {
    return response.results;
  }

  if (Array.isArray(response?.data)) {
    return response.data;
  }

  if (
    Array.isArray(
      response?.data?.results,
    )
  ) {
    return response.data.results;
  }

  return [];
}


// =========================================================
// ADDRESS → FORM
// =========================================================

function addressToForm(address) {
  if (!address) {
    return {
      ...EMPTY_FORM,
    };
  }

  return {
    title:
      address.title ||
      "",

    name:
      address.name ||
      "",

    address:
      address.address ||
      "",

    neighborhood:
      address.neighborhood ||
      "",

    city:
      address.city ||
      "",

    department:
      address.department ||
      "",

    country:
      address.country ||
      "Colombia",

    phone:
      address.phone ||
      "",

    reference:
      address.reference ||
      "",

    type:
      address.type ||
      "home",

    principal:
      Boolean(address.principal),

    latitude:
      address.latitude ??
      "",

    longitude:
      address.longitude ??
      "",

    formatted_address:
      address.formatted_address ||
      "",

    google_place_id:
      address.google_place_id ||
      "",
  };
}


// =========================================================
// CREATE INITIAL FORM
// =========================================================

function createInitialForm(
  user,
  hasAddresses,
) {
  const firstName =
    user?.first_name ||
    "";

  const lastName =
    user?.last_name ||
    "";

  const fullName =
    `${firstName} ${lastName}`.trim();

  return {
    ...EMPTY_FORM,

    name:
      fullName ||
      user?.username ||
      "",

    phone:
      user?.phone ||
      "",

    city:
      user?.city ||
      "",

    country:
      user?.country ||
      "Colombia",

    principal:
      !hasAddresses,
  };
}


// =========================================================
// FORM → PAYLOAD
// =========================================================

function formToPayload(form) {
  const payload = {
    title:
      form.title.trim(),

    name:
      form.name.trim(),

    address:
      form.address.trim(),

    neighborhood:
      form.neighborhood.trim(),

    city:
      form.city.trim(),

    department:
      form.department.trim(),

    country:
      form.country.trim() ||
      "Colombia",

    phone:
      form.phone.trim(),

    reference:
      form.reference.trim(),

    type:
      form.type,

    principal:
      Boolean(form.principal),
  };


  // =======================================================
  // LATITUDE
  // =======================================================

  if (
    form.latitude !== "" &&
    form.latitude !== null &&
    form.latitude !== undefined
  ) {
    const latitude =
      Number(form.latitude);

    if (!Number.isNaN(latitude)) {
      payload.latitude = latitude;
    }
  }


  // =======================================================
  // LONGITUDE
  // =======================================================

  if (
    form.longitude !== "" &&
    form.longitude !== null &&
    form.longitude !== undefined
  ) {
    const longitude =
      Number(form.longitude);

    if (!Number.isNaN(longitude)) {
      payload.longitude = longitude;
    }
  }


  // =======================================================
  // FORMATTED ADDRESS
  // =======================================================

  if (
    form.formatted_address &&
    form.formatted_address.trim()
  ) {
    payload.formatted_address =
      form.formatted_address.trim();
  }


  // =======================================================
  // GOOGLE PLACE ID
  // =======================================================

  if (
    form.google_place_id &&
    form.google_place_id.trim()
  ) {
    payload.google_place_id =
      form.google_place_id.trim();
  }


  return payload;
}


// =========================================================
// COMPONENT
// =========================================================

export default function Addresses() {

  // =======================================================
  // AUTH
  // =======================================================

  const {
    access,
    user,
    isAuthenticated,
    loading: authLoading,
  } = useAuth();


  // =======================================================
  // STATE
  // =======================================================

  const [
    addresses,
    setAddresses,
  ] = useState([]);

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    saving,
    setSaving,
  ] = useState(false);

  const [
    deletingId,
    setDeletingId,
  ] = useState(null);

  const [
    primaryId,
    setPrimaryId,
  ] = useState(null);

  const [
    error,
    setError,
  ] = useState("");

  const [
    success,
    setSuccess,
  ] = useState("");

  const [
    isModalOpen,
    setIsModalOpen,
  ] = useState(false);

  const [
    editingAddress,
    setEditingAddress,
  ] = useState(null);

  const [
    form,
    setForm,
  ] = useState({
    ...EMPTY_FORM,
  });


  // =======================================================
  // EDITING
  // =======================================================

  const isEditing =
    Boolean(editingAddress);


  // =======================================================
  // LOAD ADDRESSES
  // =======================================================

  const loadAddresses =
    useCallback(
      async () => {

        if (
          !access ||
          !isAuthenticated
        ) {
          setAddresses([]);
          setLoading(false);
          return;
        }

        setLoading(true);
        setError("");

        try {
          const response =
            await apiRequest(
              ADDRESSES_URL,
              {},
              access,
            );

          const data =
            extractAddressList(
              response,
            );

          const normalized =
            data
              .map(normalizeAddress)
              .filter(Boolean);

          setAddresses(
            normalized,
          );

        } catch (err) {

          console.error(
            "ADDRESSES LOAD ERROR:",
            err,
          );

          setError(
            err?.message ||
            "No fue posible cargar tus direcciones.",
          );

        } finally {
          setLoading(false);
        }
      },
      [
        access,
        isAuthenticated,
      ],
    );


  // =======================================================
  // INITIAL LOAD
  // =======================================================

  useEffect(() => {
    if (authLoading) {
      return;
    }

    loadAddresses();
  }, [
    authLoading,
    loadAddresses,
  ]);


  // =======================================================
  // SUCCESS AUTO CLEAR
  // =======================================================

  useEffect(() => {
    if (!success) {
      return undefined;
    }

    const timeout =
      setTimeout(() => {
        setSuccess("");
      }, 4000);

    return () => {
      clearTimeout(timeout);
    };
  }, [success]);


  // =======================================================
  // ESC CLOSE MODAL
  // =======================================================

  useEffect(() => {
    if (!isModalOpen) {
      return undefined;
    }

    const handleKeyDown =
      (event) => {
        if (
          event.key === "Escape" &&
          !saving
        ) {
          setIsModalOpen(false);

          setEditingAddress(
            null,
          );

          setForm({
            ...EMPTY_FORM,
          });

          setError("");
        }
      };

    window.addEventListener(
      "keydown",
      handleKeyDown,
    );

    return () => {
      window.removeEventListener(
        "keydown",
        handleKeyDown,
      );
    };
  }, [
    isModalOpen,
    saving,
  ]);


  // =======================================================
  // SORT ADDRESSES
  // =======================================================

  const sortedAddresses =
    useMemo(
      () => {
        return [
          ...addresses,
        ].sort(
          (a, b) => {
            if (
              a.principal &&
              !b.principal
            ) {
              return -1;
            }

            if (
              !a.principal &&
              b.principal
            ) {
              return 1;
            }

            return (
              Number(a.id) -
              Number(b.id)
            );
          },
        );
      },
      [addresses],
    );


  // =======================================================
  // NEW ADDRESS
  // =======================================================

  const handleNewAddress =
    () => {

      if (
        !isAuthenticated ||
        !access
      ) {
        setError(
          "Debes iniciar sesión para administrar tus direcciones.",
        );

        return;
      }

      setEditingAddress(
        null,
      );

      setForm(
        createInitialForm(
          user,
          addresses.length > 0,
        ),
      );

      setError("");
      setSuccess("");

      setIsModalOpen(true);
    };


  // =======================================================
  // EDIT ADDRESS
  // =======================================================

  const handleEdit =
    (address) => {

      if (
        !isAuthenticated ||
        !access
      ) {
        setError(
          "Debes iniciar sesión para administrar tus direcciones.",
        );

        return;
      }

      setEditingAddress(
        address,
      );

      setForm(
        addressToForm(
          address,
        ),
      );

      setError("");
      setSuccess("");

      setIsModalOpen(true);
    };


  // =======================================================
  // CLOSE MODAL
  // =======================================================

  const closeModal =
    () => {

      if (saving) {
        return;
      }

      setIsModalOpen(false);

      setEditingAddress(
        null,
      );

      setForm({
        ...EMPTY_FORM,
      });

      setError("");
    };


  // =======================================================
  // CHANGE FORM
  // =======================================================

  const handleChange =
    (event) => {

      const {
        name,
        value,
        type,
        checked,
      } = event.target;

      setForm(
        (current) => ({
          ...current,

          [name]:
            type === "checkbox"
              ? checked
              : value,
        }),
      );
    };


  // =======================================================
  // SAVE ADDRESS
  // =======================================================

  const handleSubmit =
    async (event) => {

      event.preventDefault();

      if (
        !access ||
        !isAuthenticated
      ) {
        setError(
          "Tu sesión no es válida. Inicia sesión nuevamente.",
        );

        return;
      }

      setError("");
      setSuccess("");
      setSaving(true);

      try {

        const payload =
          formToPayload(
            form,
          );


        // =================================================
        // UPDATE
        // =================================================

        if (isEditing) {

          await apiRequest(
            `${ADDRESSES_URL}${editingAddress.id}/`,
            {
              method: "PATCH",

              body:
                JSON.stringify(
                  payload,
                ),
            },
            access,
          );

        }


        // =================================================
        // CREATE
        // =================================================

        else {

          await apiRequest(
            ADDRESSES_URL,
            {
              method: "POST",

              body:
                JSON.stringify(
                  payload,
                ),
            },
            access,
          );
        }


        // =================================================
        // RELOAD FROM DJANGO
        // =================================================

        await loadAddresses();


        // =================================================
        // CLOSE MODAL
        // =================================================

        setIsModalOpen(false);

        setEditingAddress(
          null,
        );

        setForm({
          ...EMPTY_FORM,
        });


        // =================================================
        // SUCCESS
        // =================================================

        setSuccess(
          isEditing
            ? "Dirección actualizada correctamente."
            : "Dirección creada correctamente.",
        );

      } catch (err) {

        console.error(
          "ADDRESSES SAVE ERROR:",
          err,
        );

        setError(
          err?.message ||
          "No fue posible guardar la dirección.",
        );

      } finally {
        setSaving(false);
      }
    };


  // =======================================================
  // SET PRIMARY
  // =======================================================

  const handleSetPrimary =
    async (address) => {

      if (address.principal) {
        return;
      }

      if (
        !access ||
        !isAuthenticated
      ) {
        setError(
          "Debes iniciar sesión para realizar esta acción.",
        );

        return;
      }

      setPrimaryId(
        address.id,
      );

      setError("");
      setSuccess("");

      try {

        await apiRequest(
          `${ADDRESSES_URL}${address.id}/set-primary/`,
          {
            method: "POST",
          },
          access,
        );

        await loadAddresses();

        setSuccess(
          "Dirección principal actualizada.",
        );

      } catch (err) {

        console.error(
          "ADDRESSES PRIMARY ERROR:",
          err,
        );

        setError(
          err?.message ||
          "No fue posible establecer la dirección principal.",
        );

      } finally {

        setPrimaryId(
          null,
        );
      }
    };


  // =======================================================
  // DELETE
  // =======================================================

  const handleDelete =
    async (address) => {

      if (
        !access ||
        !isAuthenticated
      ) {
        setError(
          "Debes iniciar sesión para realizar esta acción.",
        );

        return;
      }

      const confirmed =
        window.confirm(
          `¿Seguro que deseas eliminar la dirección "${address.title}"?`,
        );

      if (!confirmed) {
        return;
      }

      setDeletingId(
        address.id,
      );

      setError("");
      setSuccess("");

      try {

        await apiRequest(
          `${ADDRESSES_URL}${address.id}/`,
          {
            method: "DELETE",
          },
          access,
        );

        await loadAddresses();

        setSuccess(
          "Dirección eliminada correctamente.",
        );

      } catch (err) {

        console.error(
          "ADDRESSES DELETE ERROR:",
          err,
        );

        setError(
          err?.message ||
          "No fue posible eliminar la dirección.",
        );

      } finally {

        setDeletingId(
          null,
        );
      }
    };


  // =======================================================
  // AUTH LOADING
  // =======================================================

  if (authLoading) {
    return (
      <ProfilePageLayout
        title="Direcciones"
        subtitle="Administra tus direcciones de entrega."
        icon={MapPin}
      >
        <section className="addresses-page">

          <div className="addresses-loading">

            <div className="addresses-loading-spinner" />

            <span className="addresses-loading-text">
              Verificando tu sesión...
            </span>

          </div>

        </section>
      </ProfilePageLayout>
    );
  }


  // =======================================================
  // NOT AUTHENTICATED
  // =======================================================

  if (!isAuthenticated) {
    return (
      <ProfilePageLayout
        title="Direcciones"
        subtitle="Administra tus direcciones de entrega."
        icon={MapPin}
      >
        <section className="addresses-page">

          <div className="addresses-empty">

            <div className="addresses-empty-icon">

              <AlertCircle
                size={30}
              />

            </div>

            <h3 className="addresses-empty-title">
              Inicia sesión
            </h3>

            <p className="addresses-empty-text">
              Necesitas iniciar sesión
              para administrar tus
              direcciones de entrega.
            </p>

          </div>

        </section>
      </ProfilePageLayout>
    );
  }


  // =======================================================
  // MAIN RENDER
  // =======================================================

  return (
    <ProfilePageLayout
      title="Direcciones"
      subtitle="Administra tus direcciones de entrega."
      icon={MapPin}
    >

      <section className="addresses-page">

        {/* =================================================
            HEADER
        ================================================= */}

        <header className="addresses-header">

          <div className="addresses-header-content">

            <span className="addresses-kicker">
              DEBUTS CROP
            </span>

            <h1 className="addresses-title">
              MIS DIRECCIONES
            </h1>

            <p className="addresses-subtitle">
              Administra tus direcciones
              de entrega y selecciona
              la dirección principal
              para tus pedidos.
            </p>

          </div>


          <button
            type="button"
            className="addresses-add-button"
            onClick={handleNewAddress}
          >

            <Plus
              size={18}
            />

            NUEVA DIRECCIÓN

          </button>

        </header>


        {/* =================================================
            SUCCESS
        ================================================= */}

        {success && (
          <div className="addresses-alert addresses-alert-success">

            <CheckCircle2
              size={18}
            />

            <span>
              {success}
            </span>

          </div>
        )}


        {/* =================================================
            ERROR
        ================================================= */}

        {error &&
          !isModalOpen && (
            <div className="addresses-alert addresses-alert-error">

              <AlertCircle
                size={18}
              />

              <span>
                {error}
              </span>

            </div>
          )}


        {/* =================================================
            LOADING
        ================================================= */}

        {loading && (
          <div className="addresses-loading">

            <div className="addresses-loading-spinner" />

            <span className="addresses-loading-text">
              Cargando tus direcciones...
            </span>

          </div>
        )}


        {/* =================================================
            EMPTY
        ================================================= */}

        {!loading &&
          sortedAddresses.length === 0 && (
            <div className="addresses-empty">

              <div className="addresses-empty-icon">

                <MapPin
                  size={30}
                />

              </div>

              <h3 className="addresses-empty-title">
                No tienes direcciones
              </h3>

              <p className="addresses-empty-text">
                Agrega tu primera dirección
                para utilizarla durante
                el proceso de compra.
              </p>

              <button
                type="button"
                className="addresses-add-button"
                onClick={handleNewAddress}
              >

                <Plus
                  size={17}
                />

                AGREGAR DIRECCIÓN

              </button>

            </div>
          )}


        {/* =================================================
            ADDRESS GRID
        ================================================= */}

        {!loading &&
          sortedAddresses.length > 0 && (
            <div className="addresses-grid">

              {sortedAddresses.map(
                (address) => {

                  const AddressIcon =
                    getAddressIcon(
                      address.type,
                    );

                  const isDeleting =
                    deletingId ===
                    address.id;

                  const isSettingPrimary =
                    primaryId ===
                    address.id;

                  return (
                    <article
                      key={address.id}
                      className={`address-card ${
                        address.principal
                          ? "is-primary"
                          : ""
                      }`}
                    >

                      {/* =================================
                          CARD HEADER
                      ================================= */}

                      <div className="address-card-header">

                        <div className="address-card-heading">

                          <div className="address-card-icon">

                            <AddressIcon
                              size={21}
                            />

                          </div>

                          <div>

                            <h3 className="address-card-title">
                              {address.title}
                            </h3>

                            <div className="address-card-type">
                              {address.typeLabel}
                            </div>

                          </div>

                        </div>


                        {address.principal && (
                          <span className="address-primary-badge">

                            <Star
                              size={11}
                              fill="currentColor"
                            />

                            PRINCIPAL

                          </span>
                        )}

                      </div>


                      {/* =================================
                          INFORMATION
                      ================================= */}

                      <div className="address-card-info">

                        {/* DESTINATARIO */}

                        {address.name && (
                          <div className="address-info-row">

                            <UserRound
                              size={16}
                              className="address-info-icon"
                            />

                            <div className="address-info-content">

                              <span className="address-info-label">
                                Destinatario
                              </span>

                              <span className="address-info-value">
                                {address.name}
                              </span>

                            </div>

                          </div>
                        )}


                        {/* ADDRESS */}

                        {address.address && (
                          <div className="address-info-row">

                            <MapPin
                              size={16}
                              className="address-info-icon"
                            />

                            <div className="address-info-content">

                              <span className="address-info-label">
                                Dirección
                              </span>

                              <span className="address-info-value">
                                {address.address}
                              </span>

                            </div>

                          </div>
                        )}


                        {/* NEIGHBORHOOD */}

                        {address.neighborhood && (
                          <div className="address-info-row">

                            <MapPinned
                              size={16}
                              className="address-info-icon"
                            />

                            <div className="address-info-content">

                              <span className="address-info-label">
                                Barrio
                              </span>

                              <span className="address-info-value secondary">
                                {address.neighborhood}
                              </span>

                            </div>

                          </div>
                        )}


                        {/* CITY / DEPARTMENT */}

                        {(address.city ||
                          address.department) && (
                          <div className="address-info-row">

                            <Navigation
                              size={16}
                              className="address-info-icon"
                            />

                            <div className="address-info-content">

                              <span className="address-info-label">
                                Ubicación
                              </span>

                              <span className="address-info-value">

                                {address.city}

                                {address.department
                                  ? `, ${address.department}`
                                  : ""}

                              </span>

                            </div>

                          </div>
                        )}


                        {/* PHONE */}

                        {address.phone && (
                          <div className="address-info-row">

                            <Phone
                              size={16}
                              className="address-info-icon"
                            />

                            <div className="address-info-content">

                              <span className="address-info-label">
                                Teléfono
                              </span>

                              <span className="address-info-value secondary">
                                {address.phone}
                              </span>

                            </div>

                          </div>
                        )}


                        {/* REFERENCE */}

                        {address.reference && (
                          <div className="address-reference">

                            <span className="address-reference-label">
                              Referencia de entrega
                            </span>

                            <span>
                              {address.reference}
                            </span>

                          </div>
                        )}


                        {/* GEO DATA */}

                        {(address.latitude ||
                          address.longitude) && (
                          <div className="address-location-data">

                            {address.latitude && (
                              <span className="address-location-tag">
                                LAT {address.latitude}
                              </span>
                            )}

                            {address.longitude && (
                              <span className="address-location-tag">
                                LNG {address.longitude}
                              </span>
                            )}

                          </div>
                        )}

                      </div>


                      {/* =================================
                          ACTIONS
                      ================================= */}

                      <div className="address-card-actions">

                        {!address.principal ? (
                          <button
                            type="button"
                            className="address-action-button primary"
                            onClick={() =>
                              handleSetPrimary(
                                address,
                              )
                            }
                            disabled={
                              isSettingPrimary ||
                              isDeleting
                            }
                          >

                            {isSettingPrimary ? (
                              <span className="address-save-spinner" />
                            ) : (
                              <Star
                                size={15}
                              />
                            )}

                            {isSettingPrimary
                              ? "Actualizando..."
                              : "Principal"}

                          </button>
                        ) : (
                          <button
                            type="button"
                            className="address-action-button primary"
                            disabled
                          >

                            <CheckCircle2
                              size={15}
                            />

                            Dirección principal

                          </button>
                        )}


                        {/* EDIT */}

                        <button
                          type="button"
                          className="address-action-button edit"
                          onClick={() =>
                            handleEdit(
                              address,
                            )
                          }
                          disabled={
                            isDeleting ||
                            isSettingPrimary
                          }
                        >

                          <Pencil
                            size={15}
                          />

                          Editar

                        </button>


                        {/* DELETE */}

                        <button
                          type="button"
                          className="address-action-button delete"
                          onClick={() =>
                            handleDelete(
                              address,
                            )
                          }
                          disabled={
                            isDeleting ||
                            isSettingPrimary
                          }
                        >

                          {isDeleting ? (
                            <span className="address-save-spinner" />
                          ) : (
                            <Trash2
                              size={15}
                            />
                          )}

                          {isDeleting
                            ? "Eliminando..."
                            : "Eliminar"}

                        </button>

                      </div>

                    </article>
                  );
                },
              )}

            </div>
          )}

      </section>


      {/* =====================================================
          MODAL
      ===================================================== */}

      {isModalOpen && (
        <div
          className="address-modal-backdrop"
          onMouseDown={(event) => {

            if (
              event.target ===
              event.currentTarget
            ) {
              closeModal();
            }

          }}
        >

          <div
            className="address-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="address-modal-title"
            onMouseDown={(event) =>
              event.stopPropagation()
            }
          >

            {/* ===============================================
                HEADER
            =============================================== */}

            <div className="address-modal-header">

              <div className="address-modal-heading">

                <span className="address-modal-kicker">
                  DEBUTS CROP · PROFILE
                </span>

                <h2
                  id="address-modal-title"
                  className="address-modal-title"
                >
                  {isEditing
                    ? "EDITAR DIRECCIÓN"
                    : "NUEVA DIRECCIÓN"}
                </h2>

              </div>


              <button
                type="button"
                className="address-modal-close"
                onClick={closeModal}
                disabled={saving}
                aria-label="Cerrar ventana"
              >

                <X
                  size={20}
                />

              </button>

            </div>


            {/* ===============================================
                BODY
            =============================================== */}

            <div className="address-modal-body">

              {error && (
                <div className="address-form-error">

                  <AlertCircle
                    size={17}
                  />

                  <span>
                    {error}
                  </span>

                </div>
              )}


              <form
                id="address-form"
                className="address-form"
                onSubmit={handleSubmit}
              >

                {/* =========================================
                    BASIC DATA
                ========================================= */}

                <div className="address-form-grid">

                  <div className="address-form-group">

                    <label
                      className="address-form-label"
                      htmlFor="address-title"
                    >

                      Nombre de la dirección

                      <span className="address-form-required">
                        *
                      </span>

                    </label>

                    <input
                      id="address-title"
                      className="address-form-input"
                      name="title"
                      type="text"
                      value={form.title}
                      onChange={handleChange}
                      placeholder="Ej. Casa"
                      required
                      maxLength={100}
                      autoComplete="off"
                    />

                  </div>


                  <div className="address-form-group">

                    <label
                      className="address-form-label"
                      htmlFor="address-type"
                    >
                      Tipo
                    </label>

                    <select
                      id="address-type"
                      className="address-form-select"
                      name="type"
                      value={form.type}
                      onChange={handleChange}
                    >

                      <option value="home">
                        Casa
                      </option>

                      <option value="work">
                        Trabajo
                      </option>

                      <option value="other">
                        Otra
                      </option>

                    </select>

                  </div>

                </div>


                {/* =========================================
                    NAME
                ========================================= */}

                <div className="address-form-group">

                  <label
                    className="address-form-label"
                    htmlFor="address-name"
                  >

                    Nombre del destinatario

                    <span className="address-form-required">
                      *
                    </span>

                  </label>

                  <input
                    id="address-name"
                    className="address-form-input"
                    name="name"
                    type="text"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="Nombre completo"
                    required
                    maxLength={150}
                    autoComplete="name"
                  />

                </div>


                {/* =========================================
                    PHONE
                ========================================= */}

                <div className="address-form-group">

                  <label
                    className="address-form-label"
                    htmlFor="address-phone"
                  >
                    Teléfono
                  </label>

                  <input
                    id="address-phone"
                    className="address-form-input"
                    name="phone"
                    type="tel"
                    value={form.phone}
                    onChange={handleChange}
                    placeholder="+57 300 000 0000"
                    maxLength={30}
                    autoComplete="tel"
                  />

                </div>


                {/* =========================================
                    ADDRESS
                ========================================= */}

                <div className="address-form-group">

                  <label
                    className="address-form-label"
                    htmlFor="address-address"
                  >

                    Dirección

                    <span className="address-form-required">
                      *
                    </span>

                  </label>

                  <input
                    id="address-address"
                    className="address-form-input"
                    name="address"
                    type="text"
                    value={form.address}
                    onChange={handleChange}
                    placeholder="Carrera 43 # 10-05"
                    required
                    maxLength={255}
                    autoComplete="street-address"
                  />

                </div>


                {/* =========================================
                    NEIGHBORHOOD
                ========================================= */}

                <div className="address-form-group">

                  <label
                    className="address-form-label"
                    htmlFor="address-neighborhood"
                  >
                    Barrio
                  </label>

                  <input
                    id="address-neighborhood"
                    className="address-form-input"
                    name="neighborhood"
                    type="text"
                    value={form.neighborhood}
                    onChange={handleChange}
                    placeholder="Ej. El Poblado"
                    maxLength={150}
                    autoComplete="address-level3"
                  />

                </div>


                {/* =========================================
                    CITY / DEPARTMENT
                ========================================= */}

                <div className="address-form-grid">

                  <div className="address-form-group">

                    <label
                      className="address-form-label"
                      htmlFor="address-city"
                    >

                      Ciudad

                      <span className="address-form-required">
                        *
                      </span>

                    </label>

                    <input
                      id="address-city"
                      className="address-form-input"
                      name="city"
                      type="text"
                      value={form.city}
                      onChange={handleChange}
                      placeholder="Medellín"
                      required
                      maxLength={100}
                      autoComplete="address-level2"
                    />

                  </div>


                  <div className="address-form-group">

                    <label
                      className="address-form-label"
                      htmlFor="address-department"
                    >

                      Departamento

                      <span className="address-form-required">
                        *
                      </span>

                    </label>

                    <input
                      id="address-department"
                      className="address-form-input"
                      name="department"
                      type="text"
                      value={form.department}
                      onChange={handleChange}
                      placeholder="Antioquia"
                      required
                      maxLength={100}
                      autoComplete="address-level1"
                    />

                  </div>

                </div>


                {/* =========================================
                    COUNTRY
                ========================================= */}

                <div className="address-form-group">

                  <label
                    className="address-form-label"
                    htmlFor="address-country"
                  >

                    País

                    <span className="address-form-required">
                      *
                    </span>

                  </label>

                  <input
                    id="address-country"
                    className="address-form-input"
                    name="country"
                    type="text"
                    value={form.country}
                    onChange={handleChange}
                    placeholder="Colombia"
                    required
                    maxLength={100}
                    autoComplete="country-name"
                  />

                </div>


                {/* =========================================
                    REFERENCE
                ========================================= */}

                <div className="address-form-group">

                  <label
                    className="address-form-label"
                    htmlFor="address-reference"
                  >
                    Referencia de entrega
                  </label>

                  <textarea
                    id="address-reference"
                    className="address-form-textarea"
                    name="reference"
                    value={form.reference}
                    onChange={handleChange}
                    placeholder="Ej. Casa blanca frente al parque..."
                    rows={4}
                    maxLength={500}
                  />

                </div>


                {/* =========================================
                    PRINCIPAL
                ========================================= */}

                <label
                  className="address-principal-control"
                  htmlFor="address-principal"
                >

                  <input
                    id="address-principal"
                    className="address-principal-checkbox"
                    type="checkbox"
                    name="principal"
                    checked={
                      Boolean(
                        form.principal,
                      )
                    }
                    onChange={handleChange}
                  />

                  <div className="address-principal-content">

                    <span className="address-principal-title">
                      Usar como dirección principal
                    </span>

                    <span className="address-principal-description">
                      Será utilizada por defecto
                      para tus pedidos.
                    </span>

                  </div>

                </label>


                {/* =========================================
                    GEO INFO
                ========================================= */}

                {(form.latitude ||
                  form.longitude ||
                  form.formatted_address) && (
                  <div className="address-geo-box">

                    <div className="address-geo-title">

                      <Navigation
                        size={14}
                      />

                      Ubicación geográfica

                    </div>

                    <p className="address-geo-text">

                      Esta dirección contiene
                      información de ubicación
                      obtenida mediante
                      geolocalización.

                    </p>

                  </div>
                )}

              </form>

            </div>


            {/* ===============================================
                FOOTER
            =============================================== */}

            <div className="address-modal-footer">

              <button
                type="button"
                className="address-modal-button cancel"
                onClick={closeModal}
                disabled={saving}
              >
                CANCELAR
              </button>


              <button
                type="submit"
                form="address-form"
                className="address-modal-button save"
                disabled={saving}
              >

                {saving ? (
                  <>

                    <span className="address-save-spinner" />

                    GUARDANDO...

                  </>
                ) : (
                  <>

                    <Save
                      size={16}
                    />

                    {isEditing
                      ? "GUARDAR CAMBIOS"
                      : "GUARDAR DIRECCIÓN"}

                  </>
                )}

              </button>

            </div>

          </div>

        </div>
      )}

    </ProfilePageLayout>
  );
}